import { useState } from "react";
import { NavLink, Outlet, useNavigate, useLocation } from "react-router-dom";
import {
  LayoutDashboard, Building2, Users, HandCoins, FileCheck2,
  BarChart3, LogOut, ChevronRight, Menu, KeyRound,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Sheet,
  SheetContent,
  SheetTrigger,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import { VisuallyHidden } from "@radix-ui/react-visually-hidden";
import ChangePasswordDialog from "@/components/ChangePasswordDialog";

const ADMIN_NAV = [
  { to: "/dashboard", label: "Dashboard", icon: LayoutDashboard },
  { to: "/empresas", label: "Empresas", icon: Building2 },
  { to: "/empleados", label: "Empleados", icon: Users },
  { to: "/adelantos", label: "Adelantos", icon: HandCoins },
  { to: "/liquidaciones", label: "Liquidaciones", icon: FileCheck2 },
  { to: "/reportes", label: "Reportes", icon: BarChart3 },
];

const COMPANY_NAV = [
  { to: "/dashboard", label: "Panel", icon: LayoutDashboard },
  { to: "/empleados", label: "Empleados", icon: Users },
  { to: "/adelantos", label: "Adelantos", icon: HandCoins },
];

function NavItems({ items, onNavigate }) {
  return (
    <>
      {items.map(({ to, label, icon: Icon }) => (
        <NavLink
          key={to}
          to={to}
          onClick={onNavigate}
          data-testid={`nav-${label.toLowerCase()}`}
          className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
        >
          <Icon className="w-[18px] h-[18px]" />
          <span>{label}</span>
        </NavLink>
      ))}
    </>
  );
}

export default function Layout() {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const location = useLocation();
  const isAdmin = user?.role === "admin";
  const items = isAdmin ? ADMIN_NAV : COMPANY_NAV;
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [pwOpen, setPwOpen] = useState(false);

  const currentLabel = items.find((i) => location.pathname.startsWith(i.to))?.label || "NomiDiem";

  const handleLogout = async () => {
    await logout();
    nav("/login");
  };

  return (
    <div className="min-h-screen flex bg-[#F8FAFC]">
      {/* Desktop sidebar */}
      <aside className="hidden md:flex md:w-[260px] flex-col border-r border-[#E2E8F0] bg-white">
        <div className="h-16 px-5 flex items-center border-b border-[#E2E8F0]">
          <div className="flex items-center gap-2.5">
            <div className="w-8 h-8 rounded-md bg-[#1D9E75] flex items-center justify-center">
              <span className="text-white font-bold text-sm tracking-tight">ND</span>
            </div>
            <div>
              <div className="text-[15px] font-bold text-[#0F172A] tracking-tight leading-none">NomiDiem</div>
              <div className="text-[10px] text-[#64748B] uppercase tracking-[0.15em] mt-0.5">
                {isAdmin ? "Control Panel" : "Portal RRHH"}
              </div>
            </div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-0.5">
          <NavItems items={items} />
        </nav>
        <div className="p-3 border-t border-[#E2E8F0]">
          <div className="px-3 py-2.5 rounded-md bg-[#F8FAFC]">
            <div className="text-[11px] text-[#64748B] uppercase tracking-wider font-medium">
              {isAdmin ? "Administrador" : "RRHH Empresa"}
            </div>
            <div className="text-sm font-semibold text-[#0F172A] mt-0.5 truncate">{user?.name}</div>
            <div className="text-xs text-[#64748B] truncate">{user?.email}</div>
          </div>
          <button
            data-testid="logout-btn"
            onClick={handleLogout}
            className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm text-[#475569] hover:bg-[#F1F5F9] rounded-md transition-colors"
          >
            <LogOut className="w-4 h-4" />
            Cerrar sesión
          </button>
        </div>
      </aside>

      {/* Main */}
      <main className="flex-1 min-w-0">
        <header className="h-16 border-b border-[#E2E8F0] bg-white/90 backdrop-blur-md sticky top-0 z-20 px-4 md:px-6 flex items-center justify-between gap-3">
          <div className="flex items-center gap-2 min-w-0">
            {/* Mobile hamburger */}
            <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
              <SheetTrigger asChild>
                <button
                  data-testid="mobile-menu-btn"
                  aria-label="Abrir menú"
                  className="md:hidden inline-flex items-center justify-center w-9 h-9 rounded-md text-[#0F172A] hover:bg-[#F1F5F9] transition-colors"
                >
                  <Menu className="w-5 h-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="left"
                className="w-[280px] p-0 bg-white flex flex-col"
                data-testid="mobile-nav-drawer"
              >
                <VisuallyHidden>
                  <SheetTitle>Menú de navegación</SheetTitle>
                  <SheetDescription>Selecciona una sección para continuar.</SheetDescription>
                </VisuallyHidden>
                <div className="h-16 px-5 flex items-center border-b border-[#E2E8F0]">
                  <div className="flex items-center gap-2.5">
                    <div className="w-8 h-8 rounded-md bg-[#1D9E75] flex items-center justify-center">
                      <span className="text-white font-bold text-sm tracking-tight">ND</span>
                    </div>
                    <div>
                      <div className="text-[15px] font-bold text-[#0F172A] tracking-tight leading-none">NomiDiem</div>
                      <div className="text-[10px] text-[#64748B] uppercase tracking-[0.15em] mt-0.5">
                        {isAdmin ? "Control Panel" : "Portal RRHH"}
                      </div>
                    </div>
                  </div>
                </div>
                <nav className="flex-1 p-3 space-y-0.5 overflow-y-auto">
                  <NavItems items={items} onNavigate={() => setMobileNavOpen(false)} />
                </nav>
                <div className="p-3 border-t border-[#E2E8F0]">
                  <div className="px-3 py-2.5 rounded-md bg-[#F8FAFC]">
                    <div className="text-[11px] text-[#64748B] uppercase tracking-wider font-medium">
                      {isAdmin ? "Administrador" : "RRHH Empresa"}
                    </div>
                    <div className="text-sm font-semibold text-[#0F172A] mt-0.5 truncate">{user?.name}</div>
                    <div className="text-xs text-[#64748B] truncate">{user?.email}</div>
                  </div>
                  <button
                    data-testid="mobile-logout-btn"
                    onClick={() => { setMobileNavOpen(false); handleLogout(); }}
                    className="mt-2 w-full flex items-center gap-2 px-3 py-2 text-sm text-[#B91C1C] hover:bg-rose-50 rounded-md transition-colors"
                  >
                    <LogOut className="w-4 h-4" />
                    Cerrar sesión
                  </button>
                </div>
              </SheetContent>
            </Sheet>

            <div className="hidden md:flex items-center gap-2 text-sm text-[#64748B]">
              <span className="font-semibold text-[#0F172A]">NomiDiem</span>
              <ChevronRight className="w-3.5 h-3.5" />
              <span>{isAdmin ? "Vista Administrador" : "Portal Empresa"}</span>
            </div>
            <div className="md:hidden text-base font-semibold text-[#0F172A] truncate">
              {currentLabel}
            </div>
          </div>
          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex px-2.5 py-1 rounded-full bg-[#D1FAE5] text-[#147D5C] text-[11px] font-semibold uppercase tracking-wide">
              {isAdmin ? "Admin" : "RRHH"}
            </span>
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="header-user-avatar"
                  aria-label="Menú de usuario"
                  className="w-9 h-9 rounded-full bg-[#1D9E75] text-white flex items-center justify-center text-sm font-semibold hover:bg-[#147D5C] focus:outline-none focus:ring-2 focus:ring-[#1D9E75] focus:ring-offset-2 transition-colors"
                >
                  {user?.name?.[0]?.toUpperCase() || "?"}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                sideOffset={8}
                className="w-64 bg-white"
                data-testid="header-user-menu"
              >
                <DropdownMenuLabel className="font-normal">
                  <div className="flex flex-col gap-0.5 py-1">
                    <span className="text-sm font-semibold text-[#0F172A] truncate" data-testid="menu-user-name">
                      {user?.name || "—"}
                    </span>
                    <span className="text-xs text-[#64748B] truncate" data-testid="menu-user-email">
                      {user?.email || "—"}
                    </span>
                    <span className="mt-1.5 inline-flex self-start px-2 py-0.5 rounded-full bg-[#D1FAE5] text-[#147D5C] text-[10px] font-semibold uppercase tracking-wide">
                      {isAdmin ? "Administrador" : "RRHH Empresa"}
                    </span>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-change-password-btn"
                  onClick={() => setPwOpen(true)}
                  className="cursor-pointer text-[#0F172A] focus:bg-[#F1F5F9]"
                >
                  <KeyRound className="w-4 h-4 mr-2" />
                  Cambiar contraseña
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem
                  data-testid="menu-logout-btn"
                  onClick={handleLogout}
                  className="text-[#B91C1C] focus:text-[#B91C1C] focus:bg-rose-50 cursor-pointer"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Cerrar sesión
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </header>
        <div className="p-4 md:p-8 fade-in">
          <Outlet />
        </div>
      </main>
      <ChangePasswordDialog open={pwOpen} onOpenChange={setPwOpen} />
    </div>
  );
}
